import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, map, take } from 'rxjs';
import { UserStore } from '../../user/store/user.store';
import { BillingAccessService } from '../billing-access.service';

/**
 * /billing: the active profile must be Freely or FRB and the user must hold
 * BILLING_VIEW there (or be a system admin). Otherwise → /access-denied.
 *
 * Like PermissionGuard, waits for the role-group map when the user's roles are
 * GROUP_* markers, or a deep link would be refused before the map arrives.
 */
export const billingGuard: CanActivateFn = () => {
  const access = inject(BillingAccessService);
  const userStore = inject(UserStore);
  const router = inject(Router);

  const evaluate = (): boolean => {
    if (access.canView()) return true;
    router.navigate(['/access-denied']);
    return false;
  };

  const roles = userStore.activeProfile()?.roles ?? [];
  const waitForGroups =
    roles.some((r) => r.startsWith('GROUP_')) && Object.keys(userStore.roleGroupsMap()).length === 0;
  if (!waitForGroups) return evaluate();

  return toObservable(userStore.roleGroupsMap).pipe(
    filter((groups) => Object.keys(groups).length > 0),
    take(1),
    map(() => evaluate())
  );
};
